import { effect, Injectable, signal } from '@angular/core';
import { KopConfigService } from '../core/kop-config.service';
import { JSBridgeService } from '../core/jsbridge.service';
import { AnalogIn, AnalogOut, AssignementType, DigitalIn, DigitalOut, IOBaseType, IODirection, IOType, KanaalUpdate, ModBusOut, RangedKanaal, RangedKanaalAssign } from '../props/PropTypes';
import { Alarm, analogeMeting, cAfdelingWM, cKC_Afdeling, cKC_Alarm, cKC_LosDef, cKC_LosDefs, cKC_Uren, cKopConfig, cKopConfigModules, eKanaalType, Losdef, sBijzPrg, Servo, sKCParam } from '@wasm/KopWeb';
import { wasmMapForEach, wasmMapToRecord, wasmVectorForEach, wasmVectorToArray } from '../utils/wasmVector';
import { AfdMeta, MetaService } from '../core/meta.service';
import { assignmentKey, kanaalLabel, rangedKanaalLabel } from './kanaal.utils';
import { aiTypeToKanaalType } from '../utils/AITypes';
import { TranslateService } from '../i18n/translate.service';
import { sub } from 'date-fns';
import { type } from 'os';

export type KSelectorPresentationType = "compact" | "detail" | "hardware";

@Injectable({
  providedIn: null
})
export class KanaalService {

  constructor(public jsBridgeService: JSBridgeService, private metaService: MetaService, private translateService: TranslateService) {
  }

  public prefferedPresentation = signal<KSelectorPresentationType>("compact");

  public kanaalLabel(kanaal: number, kType: IOType): string {
    return kanaalLabel(kanaal, kType);
  }

  public rangedKanaalLabel(rangedKanaal: RangedKanaal): string {
    return rangedKanaalLabel(rangedKanaal);
  }

  findMinMaxKanaal(kanalen: RangedKanaalAssign[], type: IOType) {
    let max = 0;
    let min = 1000;
    kanalen.filter(k => k.rangedKnaal.ioType.equals(type)).forEach(k => {
      let tmMax = k.rangedKnaal.start + k.rangedKnaal.range;
      if (tmMax >= max) {
        max = tmMax;
      }
      if (k.rangedKnaal.start < min) {
        min = k.rangedKnaal.start;
      }
    });
    return { min, max };
  }

  applyUpdates(kfg: cKopConfigModules, updates: KanaalUpdate[]) {
    let afdelings = wasmVectorToArray(kfg.afdelings);
    for (let afd of afdelings) {
      this.applyAfdUpdates(afd.afdeling(), updates);
    }
  }

  applyAfdUpdates(afdWM: cKC_Afdeling, updates: KanaalUpdate[], moveInvtab: boolean = true) {
    let metas = this.metaService.afdelingMeta(afdWM);

    this.applyLosDefUpdates(afdWM.losDefs, metas, updates, moveInvtab);
    this.applyLosDefUpdates(afdWM.koeling, metas, updates, moveInvtab);
    this.applyLosDefUpdates(afdWM.droogWand, metas, updates, moveInvtab);
    this.applyLosDefUpdates(afdWM.verwarming, metas, updates, moveInvtab);

    this.applyAIUpdates(afdWM, metas, updates);
    this.applyServoUpdates(afdWM, metas, updates, moveInvtab);
    this.applyBijzPrgmUpdates(afdWM, metas, updates, moveInvtab);
    this.applyAlarmUpdates(afdWM, updates, moveInvtab);
    this.applyUrenUpdates(afdWM, updates, moveInvtab);
  }


  applyAIUpdates(afd: cKC_Afdeling, metas: AfdMeta, updates: KanaalUpdate[]) {
    for (let idx = 0; idx < afd.analogs.size(); idx++) {
      let ai = afd.analogs.at(idx);
      let aiType = this.metaService.aiTypes().find(ai.typeIndex);
      let kanaalType = aiTypeToKanaalType(aiType);
      if (AnalogIn.equals(kanaalType)) {
        let meta = metas.ais.find(m => m.index == ai.index);
        if (meta != undefined) {
          let tmp: RangedKanaal = {
            ioType: AnalogIn, start: ai.kanaal, range: ai.range
          };
          let validUpdates = this.findUpdates(tmp, updates);
          if (validUpdates != undefined) {
            ai.kanaal = ai.kanaal + validUpdates.offset;
          }
        }
      }

    }
  }

  copyInvTab(start: RangedKanaal, offset: number, move: boolean) {
    for (let i = start.start; i < start.start + start.range; i++) {
      let inv = this.getInvTab(i, start.ioType);
      this.setInvTab(i + offset, start.ioType, inv, 1);
      if (move) {
        this.setInvTab(i, start.ioType, false, 1);
      }
    }
  }

  applyLosDefUpdates(losDefs: cKC_LosDefs, metas: AfdMeta, updates: KanaalUpdate[], move: boolean = true) {
    for (let idx = 0; idx < losDefs.size(); idx++) {
      let ld = losDefs.at(idx);
      let meta = metas.losDefRaw.find(m => m.index == ld.index);
      if (meta != undefined) {
        let type = this.getIOTypeFromParamType(meta.type);
        if (type != undefined) {
          let tmp: RangedKanaal = {
            ioType: type, start: ld.waarde, range: ld.range,
          }
          let validUpdates = this.findUpdates(tmp, updates);
          if (validUpdates != undefined) {

            ld.waarde = ld.waarde + validUpdates.offset;
            this.copyInvTab(tmp, validUpdates.offset, move);
          }
        }
      }
    }
  }


  applyServoUpdates(afd: cKC_Afdeling, metas: AfdMeta, updates: KanaalUpdate[], move: boolean = true) {
    // let metas = this.metaService.afdelingMeta(afdWM);
    for (let idx = 0; idx < afd.servos.size(); idx++) {
      let s = afd.servos.at(idx);
      let type = this.getServoKanaalType({ soort: s.soort.value, uitgang: s.uitgang, stationnr: s.stationnr, modbusNetworkId: s.modbusNetworkId });
      let meta = metas.servos.find(m => m.index == s.index);
      if (type != undefined && meta != undefined) {
        let validUpdates = this.findUpdates(type, updates);
        if (validUpdates != undefined) {
          s.uitgang = s.uitgang + validUpdates.offset;
          this.copyInvTab(type, validUpdates.offset, move);

        }
      }
      for (let sp of wasmVectorToArray(s.params.keys())) {
        let param = s.params.at(sp);
        let type = this.getIOTypeFromParamType(param?.type);
        let pmeta = meta?.param.get(sp);
        if (meta != undefined && type != undefined && pmeta != undefined && type != undefined && param != undefined) {
          let tmp: RangedKanaal = {
            ioType: type, start: param.waarde, range: param.range
          };
          let validUpdates = this.findUpdates(tmp, updates);
          if (validUpdates != undefined) {
            param.waarde = param.waarde + validUpdates.offset;
            this.copyInvTab(tmp, validUpdates.offset, move);
          }
        }
      }
    }
  }

  applyBijzPrgmUpdates(afd: cKC_Afdeling, metas: AfdMeta, updates: KanaalUpdate[], move: boolean = true) {
    for (let idx = 0; idx < afd.bijzPrgs.size(); idx++) {
      let bp = afd.bijzPrgs.at(idx);
      let meta = metas.bijzPrgs.find(m => m.index == bp.index);
      for (let bpp of wasmVectorToArray(bp.params.keys())) {
        let param = bp.params.at(bpp);
        let type = this.getIOTypeFromParamType(param?.type);
        let pmeta = meta?.param.get(bpp);
        if (meta != undefined && type != undefined && pmeta != undefined && type != undefined && param != undefined) {
          let tmp: RangedKanaal = {
            ioType: type, start: param.waarde, range: param.range
          };
          let validUpdates = this.findUpdates(tmp, updates);
          if (validUpdates != undefined) {
            param.waarde = param.waarde + validUpdates.offset;
            this.copyInvTab(tmp, validUpdates.offset, move);
          }
        }
      }
    }
  }

  applyAlarmUpdates(afd: cKC_Afdeling, updates: KanaalUpdate[], move: boolean = true) {
    // let alarms = wasmVectorToArray(afd.alarms);
    let jsBridge = this.jsBridgeService.getBridgeSync();
    let alarmsMeta = wasmVectorToArray(jsBridge.getAlarms(afd.type.value))!;

    for (let idx = 0; idx < afd.alarms.size(); idx++) {
      // for (let a of alarms) {
      let a = afd.alarms.at(idx);
      let meta = alarmsMeta.find(al => al.index == a.index);
      if (meta != undefined) {
        let tmp: RangedKanaal = {
          ioType: DigitalIn, start: a.kanaal, range: 1
        };
        let validUpdates = this.findUpdates(tmp, updates);
        if (validUpdates != undefined) {
          a.kanaal = a.kanaal + validUpdates.offset;
          this.copyInvTab(tmp, validUpdates.offset, move);
        }
      }
    }
  }

  applyUrenUpdates(afd: cKC_Afdeling, updates: KanaalUpdate[], move: boolean = true) {
    let jsBridge = this.jsBridgeService.getBridgeSync();
    let uuren = afd.urens;
    let urenKeys = wasmVectorToArray(afd.urens.keys());
    for (let k of urenKeys) {
      let u = uuren.at(k);
      let tmp: RangedKanaal = {
        ioType: DigitalIn, start: u.kanaal, range: 1
      };
      let validUpdates = this.findUpdates(tmp, updates);
      if (validUpdates != undefined) {
        u.kanaal = u.kanaal + validUpdates.offset;
        this.copyInvTab(tmp, validUpdates.offset, move);
      }
    }
    
  }


  findUpdates(kanaal: RangedKanaal, updates: KanaalUpdate[]) {
    return updates.find(u => u.ranged.ioType.equals(kanaal.ioType) && u.ranged.start + u.ranged.range > kanaal.start && u.ranged.start <= kanaal.start);
  }

  getKonfigKanalen(kfg: cKopConfig) {
    let ret: Array<RangedKanaalAssign> = [];
    let afdls = kfg.afdls;
    wasmVectorForEach(afdls, afd => {
      ret = ret.concat(this.getAfdelingKanalen(afd));
    });
    return ret;
  }

  getAfdelingKanalen(afd: cKC_Afdeling) {
    let ret: Array<RangedKanaalAssign> = [];

    let metas = this.metaService.afdelingMeta(afd);


    let losdefs = this.getAfdelingLosDef(afd, afd.losDefs, metas, 'losdef');
    let droogWand = this.getAfdelingLosDef(afd, afd.droogWand, metas, 'droogwand');
    let koeling = this.getAfdelingLosDef(afd, afd.koeling, metas, 'koeling');
    let verwarming = this.getAfdelingLosDef(afd, afd.verwarming, metas, 'verwarming');
    // Lose Defs
    let aiKanalen = this.getAfdelingAIKanalen(afd, metas);
    let servoKanalen = this.getAfdelingServoKanalen(afd, metas);
    let bijProgKanalen = this.getBijzProgKanalen(afd, metas);
    let alarmenKanalen = this.getAlarmsKanalen(afd);
    let uurenKanalen = this.getUurenTellerKanalen(afd);

    // AIs
    return ret.concat(aiKanalen)
      .concat(losdefs).concat(droogWand).concat(koeling).concat(verwarming)
      .concat(servoKanalen).concat(bijProgKanalen)
      .concat(alarmenKanalen).concat(uurenKanalen);
  }

  buildAssignementInfo(type: AssignementType, afd: cKC_Afdeling, _meta: Losdef | analogeMeting | sBijzPrg | Servo | Alarm, extra: any = undefined) {
    let db = this.translateService.kopDataDBSignal();

    if (type == 'ai') {
      let meta = _meta as analogeMeting;
      return {
        index: meta.index,
        omschrijving: this.translateService.translateKopDataSync(this.translateService.kopDataDBSignal(), 'ai', meta.index, meta.omschrij?.toString()), //meta.omschr.toString(),
        type: type,
        afdRefNr: afd.refNr,
        afdeling: afd.titel.toString(),
        key: assignmentKey(type, afd.refNr, meta.index, undefined)
      }
    } else if (type == 'bijzPrg') {
      let meta = _meta as sBijzPrg;
      let bijzPrgTxt = this.translateService.translateKopDataSync(db, 'bijzprgm', meta?.index, meta?.omschr?.toString());
      let subIndex = extra as number;

      if (subIndex != undefined) {
          let pmeta = meta.param.get(subIndex);
          let bpp = subIndex;
          if (pmeta != undefined) {
            let paramTxt = this.translateService.translateKopDataSync(this.translateService.kopDataDBSignal(), 'bijzprgmparam', `${meta.index}.${(bpp + 1)}`, pmeta.omschr?.toString()); //this.translateService.translateKopDataSync(db, 'bijzprgmparam', bpp, pmeta.omschr.toString());

            return {
              index: meta.index,
              omschrijving: bijzPrgTxt + ' ' + paramTxt,
              type: type,
              afdRefNr: afd.refNr,
              afdeling: afd.titel.toString(),
              key: assignmentKey(type, afd.refNr, meta.index, subIndex)
            }
          }
          
    

      }
      return {
        index: meta.index,
        omschrijving: bijzPrgTxt,
        type: type,
        afdRefNr: afd.refNr,
        afdeling: afd.titel.toString(),
        key: assignmentKey(type, afd.refNr, meta.index, undefined)
      }
    } else if (type == 'servo') {
      let meta = _meta as Servo;
      return {
        index: meta.index,
        omschrijving: this.translateService.translateKopDataSync(this.translateService.kopDataDBSignal(), 'servo', meta.index, meta.omschr?.toString()), //meta.omschr.toString(),
        type: type,
        afdRefNr: afd.refNr,
        afdeling: afd.titel.toString(),
        key: assignmentKey(type, afd.refNr, meta.index, undefined)
      }
    } else if (type == 'servoparam' && extra != undefined) {
      let sp = extra as number;
      
      let meta = _meta as Servo;
      let pmeta = meta.param.get(sp);
      if (pmeta != undefined) {
        let servoTxt = this.translateService.translateKopDataSync(this.translateService.kopDataDBSignal(), 'servo', meta.index, meta.omschr?.toString());
        let paramTxt = this.translateService.translateKopDataSync(db, 'servoparam', `${meta.index}.${(sp + 1)}`, pmeta.omschr?.toString()); //this.translateService.translateKopDataSync(db, 'servoparam', sp, pmeta.omschr?.toString());
        return {
          index: sp,
          omschrijving: servoTxt + ' ' + paramTxt,
          type: type,
          afdRefNr: afd.refNr,
          afdeling: afd.titel.toString(),
          key: assignmentKey('servoparam', afd.refNr, meta.index, sp)
        }

      }
    } else if (type == 'alarm') {
      let meta = _meta as Alarm;
      let a = extra as cKC_Alarm;
      return {
        index: meta.index,
        omschrijving: this.getTextForAlarm(afd, a, meta), //meta.omschr.toString(),
        type: type,
        afdRefNr: afd.refNr,
        afdeling: afd.titel.toString(),
        key: assignmentKey(type, afd.refNr, meta.index, undefined)
      }
    }
    let meta = _meta as Losdef;
    return {
      index: meta.index,
      omschrijving: this.translateService.translateKopDataSync(this.translateService.kopDataDBSignal(), 'losdefomschr', meta.index, meta.omschr?.toString()), //meta.omschr.toString(),
      type: type,
      afdRefNr: afd.refNr,
      afdeling: afd.titel.toString(),
      key: assignmentKey(type, afd.refNr, meta.index, undefined)
    }
  }

  getAfdelingLosDef(afd: cKC_Afdeling, losdefs: cKC_LosDefs, metas: AfdMeta, assignementType: AssignementType) {
    let ret: Array<RangedKanaalAssign> = [];
    let db = this.translateService.kopDataDBSignal();

    wasmVectorForEach(losdefs, ld => {
      let meta = metas.losDefRaw.find(m => m.index == ld.index);
      if (meta != undefined) {
        let type = this.getIOTypeFromParamType(meta.type);
        if (type != undefined) {
          let tmp: RangedKanaalAssign = {
            rangedKnaal: { ioType: type, start: ld.waarde, range: ld.range },
            assignement: this.buildAssignementInfo(assignementType, afd, meta)
          };
          ret.push(tmp);
        }
      }

    });

    return ret;

  }
  getAfdelingAIKanalen(afd: cKC_Afdeling, metas: AfdMeta) {
    let ret: Array<RangedKanaalAssign> = [];
    let db = this.translateService.kopDataDBSignal();
    wasmVectorForEach(afd.analogs, ai => {
      let meta = metas.ais.find(m => m.index == ai.index);
      if (meta != undefined) {
        let aiType = this.metaService.aiTypes().find(ai.typeIndex);
        let kanaalType = aiTypeToKanaalType(aiType);
        let kanaalStart = ai.kanaal;
        if (!AnalogIn.equals(kanaalType)) {
          kanaalStart = kanaalStart / 2;
        }
        let tmp: RangedKanaalAssign = {
          rangedKnaal: { ioType: kanaalType, start: kanaalStart, range: ai.range },
          assignement: this.buildAssignementInfo('ai', afd, meta)
        };
        ret.push(tmp);
      }

    })
    return ret;

  }

  findModbusSlaves(slaveParam: number, afd: cKC_Afdeling, metas: AfdMeta, servo: RangedKanaal) {
    let ret: Array<RangedKanaalAssign> = [];
    let db = this.translateService.kopDataDBSignal();
    wasmVectorForEach(afd.bijzPrgs, bp => {
      let bpMeta = metas.bijzPrgs.find(m => m.index == bp.index);

      if (bpMeta?.no == slaveParam) {
        let p1 = bp.params.at(0);
        let p2 = bp.params.at(1);
        let p4 = bp.params.size() > 3 ? bp.params.at(3) : undefined;

        let tmp: RangedKanaalAssign = {
          rangedKnaal: {
            ioType: servo.ioType,
            start: p1.waarde,
            range: p2.waarde
          },
          assignement: this.buildAssignementInfo('bijzPrg', afd, bpMeta)
        };
        ret.push(tmp);
        if (p2 != undefined && p4 != undefined && p4.waarde > 0) {
          ret = ret.concat(this.findModbusSlaves(p4.waarde, afd, metas, servo));
        }
      }
    });
    return ret;
  }


  getAfdelingServoKanalen(afd: cKC_Afdeling, metas: AfdMeta) {
    let ret: Array<RangedKanaalAssign> = [];
    // let servo = wasmVectorToArray(afd.servos);
    // let metas = this.metaService.afdelingMeta(afdWM);\
    let db = this.translateService.kopDataDBSignal();
    wasmVectorForEach(afd.servos, s => {
      let type = this.getServoKanaalType({ soort: s.soort.value, uitgang: s.uitgang, stationnr: s.stationnr, modbusNetworkId: s.modbusNetworkId });
      let meta = metas.servos.find(m => m.index == s.index);
      let servoTxt = this.translateService.translateKopDataSync(db, 'servo', s.index, meta?.omschr?.toString()); //meta.omschr.toString(),
      if (type != undefined && meta != undefined) {
        let tmp: RangedKanaalAssign = {
          rangedKnaal: type,
          assignement: this.buildAssignementInfo('servo', afd, meta),

        };
        ret.push(tmp);

        if (ModBusOut.equals(type.ioType) && s.params.keys().size() == 4) {
          let lastParam = s.params.at(3);
          if (lastParam != undefined) {
            let modbusSlaves = this.findModbusSlaves(lastParam.waarde, afd, metas, type);
            ret = ret.concat(modbusSlaves);
          }
        }
      }

      wasmMapForEach(s.params, (k, v) => {
        let sp = k;
        let param = v;

        let type = this.getIOTypeFromParamType(param?.type);
        let pmeta = meta?.param.get(sp);
        if (meta != undefined && type != undefined && pmeta != undefined && type != undefined && param != undefined) {
          let paramTxt = this.translateService.translateKopDataSync(db, 'servoparam', `${s.index}.${(sp + 1)}`, pmeta.omschr?.toString()); //this.translateService.translateKopDataSync(db, 'servoparam', sp, pmeta.omschr?.toString());
          let tmp: RangedKanaalAssign = {
            rangedKnaal: { ioType: type, start: param.waarde, range: param.range },
            assignement: this.buildAssignementInfo('servoparam', afd, meta, sp)
          };
          ret.push(tmp);
        }

      });
    }
    )
    return ret;
  }

  getBijzProgKanalen(afd: cKC_Afdeling, metas: AfdMeta) {
    let ret: Array<RangedKanaalAssign> = [];
    //let bijzPrgs = wasmVectorToArray(afd.bijzPrgs);
    // let metas = this.metaService.afdelingMeta(afdWM);
    let db = this.translateService.kopDataDBSignal();

    wasmVectorForEach(afd.bijzPrgs, (bp) => {
      let meta = metas.bijzPrgs.find(m => m.index == bp.index);
      let bijzPrgTxt = this.translateService.translateKopDataSync(db, 'bijzprgm', meta?.index, meta?.omschr?.toString());
      wasmMapForEach(bp.params, (k, v) => {
        let param = v;
        let bpp = k;
        let type = this.getIOTypeFromParamType(param?.type);
        let pmeta = meta?.param.get(bpp);
        if (meta != undefined && type != undefined && pmeta != undefined && type != undefined && param != undefined) {
          let paramTxt = this.translateService.translateKopDataSync(db, 'bijzprgmparam', `${meta.index}.${(bpp + 1)}`, pmeta.omschr?.toString()); //this.translateService.translateKopDataSync(db, 'bijzprgmparam', bpp, pmeta.omschr.toString());
          let tmp: RangedKanaalAssign = {
            rangedKnaal: { ioType: type, start: param.waarde, range: param.range },
            assignement: this.buildAssignementInfo('bijzPrg', afd, meta, bpp)
            // {
            //   index: bp.index,
            //   omschrijving: bijzPrgTxt + ' ' + paramTxt,
            //   type: 'bijzPrg',
            //   afdRefNr: afd.refNr,
            //   afdeling: afd.titel.toString(),
            //   key: assignmentKey('bijzPrg', afd.refNr, bp.index, bpp)
            // }
          };
          ret.push(tmp);
        }
      });
    });

    return ret;
  }

  getTextForAlarm(afd: cKC_Afdeling, a: cKC_Alarm | undefined, meta: Alarm | undefined) {
    if (a == undefined) return "";
    if (meta == undefined) return "";
    let kopConfig = this.jsBridgeService.kopConfig();
    if (kopConfig != undefined) {
      let globalAlarmTeksten = wasmMapToRecord<string>(kopConfig.gAlarmTeksten, (e: any) => e.toString());
      let afdTeksten = wasmMapToRecord(afd.alarmTexts, (e: any) => e.toString());
      Object.entries(afdTeksten).forEach(([key, val]) => {
        globalAlarmTeksten[parseInt(key) + 20] = val;
      });
      let vrijText = globalAlarmTeksten[meta.vrijetext - 1] ?? '';
      return `${vrijText} - V(${meta.vrijetext})`;
    }
    return "";
  }

  getUurenTellerKanalen(afd: cKC_Afdeling) {
    let ret: Array<RangedKanaalAssign> = [];
    let afdUuren = wasmMapToRecord(afd.urens, (e: any) => e as cKC_Uren);
    let kopConfig = this.jsBridgeService.kopConfig();
    if (kopConfig == undefined) {
      return ret;
    }
    let texts = this.uurenTeksten(afd);
    Object.entries(afdUuren).forEach(([key, val]) => {
        let tmp: RangedKanaalAssign = {
          rangedKnaal: { ioType: DigitalIn, start: val.kanaal, range: 1 },
          assignement: {
            index: val.no,
            omschrijving:  `Urren: T(${val.no + 1}) - ${texts[val.no] ?? ''}`,
            type: 'uren',
            afdRefNr: afd.refNr,
            afdeling: afd.titel.toString(),
            key: assignmentKey('uren', afd.refNr, val.no, undefined)
          }
        };
        ret.push(tmp);
      
    });
    return ret;
    
  }

  public uurenTeksten(afd: cKC_Afdeling) {
    let afdTextsW = afd.urenTellerTexts;
    let konf = this.jsBridgeService.kopConfig();

    if (konf != undefined) {
      let texts = wasmMapToRecord(konf.gUrenTellerTeksten, e => e.toString() as string);
      if (afdTextsW != undefined) {
        let afdTexts = wasmMapToRecord(afdTextsW, e => e.toString());
        Object.entries(afdTexts).forEach(([key, val]) => {
          texts[parseInt(key) + 20] = val;
        });
      }

      return texts;
    }
    return {};

  }


  getAlarmsKanalen(afd: cKC_Afdeling) {
    let ret: Array<RangedKanaalAssign> = [];
    let jsBridge = this.jsBridgeService.getBridgeSync();
    let alarmsMetaWasm = jsBridge.getAlarms(afd.type.value);
    let alarmsMeta = wasmVectorToArray(alarmsMetaWasm)!;
    wasmVectorForEach(afd.alarms, a => {
      let meta = alarmsMeta.find(al => al.index == a.index);
      if (meta != undefined) {
        let tmp: RangedKanaalAssign = {
          rangedKnaal: { ioType: DigitalIn, start: a.kanaal, range: 1 },
          assignement: this.buildAssignementInfo('alarm', afd, meta, a)
          // {
          //   index: a.index,
          //   omschrijving: this.getTextForAlarm(afd, a, meta),
          //   type: 'alarm',
          //   afdRefNr: afd.refNr,
          //   afdeling: afd.titel.toString(),
          //   key: assignmentKey('alarm', afd.refNr, a.index, undefined)
          // }
        };
        ret.push(tmp);
      }
    });
    alarmsMetaWasm.delete();
    return ret;
  }


  getServoKanaalType(servo: { soort: number, uitgang: number, stationnr: number, modbusNetworkId: number }): RangedKanaal | undefined {
    var type: IOType;
    var wasm = this.jsBridgeService.wasmModule!;
    switch (servo.soort) {
      case wasm.eServoSoort.eSS_SERVO.value:
        return {
          ioType: DigitalOut,
          start: servo.uitgang,
          range: 2
        }
        break;
      case wasm.eServoSoort.eSS_PULS.value:
      case wasm.eServoSoort.eSS_OPDI.value:
        return {
          ioType: DigitalOut,
          start: servo.uitgang,
          range: 1
        }
        break;
      case wasm.eServoSoort.eSS_ANA0T10.value:
      case wasm.eServoSoort.eSS_ANA2T10.value:
        return {
          ioType: AnalogOut,
          start: servo.uitgang,
          range: 1
        }
        break;
      case wasm.eServoSoort.eSS_LLBUS.value:
        return {
          ioType: ModBusOut,
          start: servo.stationnr,
          network: servo.modbusNetworkId,
          range: 1
        }
    }

    return;
  }


  getIOTypeFromParamType(type?: number): IOType | undefined {
    if (type == undefined) {
      return;
    }
    let sw = type & 0x07;
    switch (sw) {
      case 0x01: return AnalogIn;    // ana in
      case 0x02: return AnalogOut;   // ana uit
      case 0x05: return DigitalIn;   // dig in
      case 0x06: return DigitalOut;   // dig uit
    }
    return;
  }

  getRangeFromParamType(type?: number): { min: number, max: number } {
    let ret = {
      min: 0,
      max: 0
    }
    if (type == undefined) {
      return ret;
    }

    let bits = type & 0b10000;
    let signed = type & 0b10000000
    if (bits) {
      if (signed) {
        ret = {
          min: -32768,
          max: 32767
        }
      } else {
        ret = {
          min: 0,
          max: 65535
        }
      }
    } else {
      if (signed) {
        ret = {
          min: -128,
          max: 127
        }
      } else {
        ret = {
          min: 0,
          max: 255
        }
      }

    }

    return ret
  }


  getKCParamType(servoParam: {
    type: number,
    waarde: number,
    range: number
  }): RangedKanaal | undefined {
    let ioType: IOType | undefined = this.getIOTypeFromParamType(servoParam.type);
    if (ioType != undefined) {
      return {
        ioType: ioType,
        range: servoParam.range,
        start: servoParam.waarde
      }
    }

    return;
  }

  ioType2Kanaal(type: IOType) {
    let wasm = this.jsBridgeService.wasmModule!;
    var kType: eKanaalType = wasm.eKanaalType.AI;
    if ((type.direction == IODirection.IN) && (type.type == IOBaseType.Digital)) {
      kType = wasm.eKanaalType.DI;
    } else if ((type.direction == IODirection.OUT) && (type.type == IOBaseType.Digital)) {
      kType = wasm.eKanaalType.DO;
    } else if ((type.direction == IODirection.OUT) && (type.type == IOBaseType.Analog)) {
      kType = wasm.eKanaalType.AO;
    }

    return kType;
  }


  getInvTab(kanaal: number, type: IOType) {
    var kType = this.ioType2Kanaal(type);
    return this.jsBridgeService.getBridgeSync().getInvTab(kanaal, kType);
  }

  setInvTab(kanaal: number, type: IOType, inv: boolean, range: number) {
    var kType = this.ioType2Kanaal(type);
    for (let k = kanaal; k < kanaal + range; k++) {
      this.jsBridgeService.getBridgeSync().setInvTab(k, kType, inv);
    }
    // this.jsBridgeService.getBridgeSync().setInvTab(kanaal, kType, inv);
  }

  getAOV(kanaal: number) {
    return this.jsBridgeService.getBridgeSync().getAOV(kanaal);
  }

  setAOV(kanaal: number, aov: boolean) {
    this.jsBridgeService.getBridgeSync().setAOV(kanaal, aov);
  }

}
